/**
 * LeftShield Portal - Frameworks Page
 * Route: /frameworks | Nav: Compliance > Frameworks
 *
 * Displays compliance frameworks with enable/disable toggles and detail drawer.
 * Includes a Control Mappings tab to view cross-framework control relationships.
 *
 * The Frameworks tab is migrated onto the List & Grid Standard local primitives — see
 * `src/pages/ApiKeysPage.tsx` (reference migration) and
 * `.specify/journals/list-grid-adoption-journal.md` (recipe).
 */
import { useEffect, useState } from 'react';
import type { FC } from 'react';
import {
  Body1,
  Body2,
  Card,
  Badge,
  makeStyles,
  tokens,
  Button,
  Switch,
  ProgressBar,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  Tab,
  TabList,
  SelectTabEvent,
  SelectTabData,
  Dropdown,
  Option,
  Spinner,
} from '@fluentui/react-components';
import {
  ChevronRightRegular,
  LinkRegular,
  DocumentRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { complianceApi, frameworksApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import type { ComplianceFramework, ControlMapping } from '../types';
import { useIsAuditor } from '../hooks/useIsAuditor';
import { useAuth } from '../features/authentication';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { ConfidenceBadge } from '../components/confidence';
import { ComplianceCoverageRadar } from '../components/compliance/ComplianceCoverageRadar';
import {
  ListPageLayout,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

/**
 * Roles allowed to enable/disable a compliance framework — mirrors
 * `McpConfigurationController.RequireAnyRole` on `POST /frameworks/{id}/enable|disable`.
 */
const FRAMEWORK_MANAGER_ROLES = ['System.Admin', 'Organization.Admin', 'TenantAdmin', 'ComplianceManager'];

/** Cache key prefix shared by the adaptive-grid's probe/page queries for the frameworks list. */
const FRAMEWORKS_QUERY_KEY = ['compliance-frameworks'] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getComplianceColor = (score: number): string => {
  if (score >= 90) return tokens.colorPaletteGreenForeground1;
  if (score >= 70) return tokens.colorPaletteYellowForeground1;
  if (score >= 50) return tokens.colorPaletteRedForeground2;
  return tokens.colorPaletteRedForeground1;
};

/** Narrows an `unknown` React Query error to detect an unauthenticated (401 / MSAL) failure. */
function isUnauthorizedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const candidate = err as { status?: unknown; message?: unknown };
  if (candidate.status === 401) return true;
  return typeof candidate.message === 'string' && candidate.message.includes('MSAL');
}

/** Wraps the (unpaged) `complianceApi.getFrameworks` response as a `PagedResult` — `useAdaptiveGridData`
 *  then applies search/sort/paging in memory (client mode), since the frameworks list is never large
 *  enough to warrant a real server-paged endpoint. */
async function fetchFrameworksPage(params: ListQueryParams): Promise<PagedResult<ComplianceFramework>> {
  const items = await complianceApi.getFrameworks();
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  nonListSection: {
    flexShrink: 0,
  },
  tabsCard: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  tabHeader: {
    flexShrink: 0,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacingHorizontalL,
  },
  mappingsSection: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: tokens.spacingHorizontalL,
  },
  scoreCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

// ---------------------------------------------------------------------------
// Strength badge colors
// ---------------------------------------------------------------------------

const getStrengthColor = (strength: string): 'success' | 'warning' | 'danger' | 'informative' => {
  switch (strength) {
    case 'Strong': return 'success';
    case 'Moderate': return 'warning';
    case 'Weak': return 'danger';
    default: return 'informative';
  }
};

// ---------------------------------------------------------------------------
// ControlMappingsTab
// ---------------------------------------------------------------------------

const ControlMappingsTab: React.FC<{
  frameworkCodes: string[];
}> = ({ frameworkCodes }) => {
  const ps = usePageStyles();
  const [sourceFramework, setSourceFramework] = useState<string>('');

  const { data: mappings, isLoading } = useQuery({
    queryKey: ['controlMappings', sourceFramework],
    queryFn: () => frameworksApi.getControlMappings(sourceFramework),
    enabled: !!sourceFramework,
  });

  const columns: TableColumnDefinition<ControlMapping>[] = [
    createTableColumn<ControlMapping>({
      columnId: 'sourceFramework',
      compare: (a, b) => a.sourceFrameworkCode.localeCompare(b.sourceFrameworkCode),
      renderHeaderCell: () => 'Source Framework',
      renderCell: (item) => <Badge appearance="outline">{item.sourceFrameworkCode}</Badge>,
    }),
    createTableColumn<ControlMapping>({
      columnId: 'sourceControl',
      compare: (a, b) => a.sourceControlId.localeCompare(b.sourceControlId),
      renderHeaderCell: () => 'Source Control',
      renderCell: (item) => <Body2 style={{ fontFamily: 'monospace' }}>{item.sourceControlId}</Body2>,
    }),
    createTableColumn<ControlMapping>({
      columnId: 'targetFramework',
      compare: (a, b) => a.targetFrameworkCode.localeCompare(b.targetFrameworkCode),
      renderHeaderCell: () => 'Target Framework',
      renderCell: (item) => <Badge appearance="outline">{item.targetFrameworkCode}</Badge>,
    }),
    createTableColumn<ControlMapping>({
      columnId: 'targetControl',
      compare: (a, b) => a.targetControlId.localeCompare(b.targetControlId),
      renderHeaderCell: () => 'Target Control',
      renderCell: (item) => <Body2 style={{ fontFamily: 'monospace' }}>{item.targetControlId}</Body2>,
    }),
    createTableColumn<ControlMapping>({
      columnId: 'strength',
      renderHeaderCell: () => 'Strength',
      renderCell: (item) => (
        <Badge appearance="tint" color={getStrengthColor(item.mappingStrength)}>
          {item.mappingStrength}
        </Badge>
      ),
    }),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
        <Body2 style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Source Framework:</Body2>
        <Dropdown
          placeholder="Select a framework"
          value={sourceFramework}
          onOptionSelect={(_, data) => setSourceFramework(data.optionValue ?? '')}
          style={{ minWidth: '200px' }}
        >
          {frameworkCodes.map((code) => (
            <Option key={code} value={code}>{code}</Option>
          ))}
        </Dropdown>
      </div>

      {!sourceFramework && (
        <Body1 className={ps.emptyState}>
          Select a source framework to view its control mappings.
        </Body1>
      )}

      {sourceFramework && isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXL }}>
          <Spinner size="small" label="Loading mappings..." />
        </div>
      )}

      {sourceFramework && !isLoading && (!mappings || mappings.length === 0) && (
        <Body1 className={ps.emptyState}>
          No control mappings configured for {sourceFramework}. Control mappings link equivalent controls across frameworks.
        </Body1>
      )}

      {mappings && mappings.length > 0 && (
        <DataGrid items={mappings} columns={columns} sortable style={{ minWidth: '700px' }}>
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<ControlMapping>>
            {({ item, rowId }) => (
              <DataGridRow<ControlMapping> key={rowId}>
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// FrameworksPage
// ---------------------------------------------------------------------------

type PageTab = 'frameworks' | 'mappings';

export const FrameworksPage: FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAuditorOnly } = useIsAuditor();
  const canManageFrameworks = user?.roles?.some((role) => FRAMEWORK_MANAGER_ROLES.includes(role)) ?? false;
  // Hidden-not-disabled: the Enabled toggle only renders for a role permitted to call
  // enable/disable AND never for an auditor-mode session, even if that user also holds a
  // manager role (Auditor Access Mode forces read-only regardless of other roles — F036).
  const showEnabledColumn = canManageFrameworks && !isAuditorOnly;

  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<PageTab>('frameworks');

  // Full, unpaginated framework set — feeds the Coverage Radar and the Control Mappings tab's
  // framework picker. Both are non-list content (siblings of the migrated list) and must never
  // be truncated by the list's search/sort/pagination state.
  const { data: allFrameworks } = useQuery({
    queryKey: ['compliance-frameworks-full'],
    queryFn: () => complianceApi.getFrameworks(),
  });
  const frameworkList = allFrameworks ?? [];
  const frameworkCodes = frameworkList.map((f) => f.code);

  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'name:asc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<ComplianceFramework>({
    queryKeyBase: FRAMEWORKS_QUERY_KEY,
    fetchPage: fetchFrameworksPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['code', 'name'],
    sortState,
  });

  const {
    items: frameworks,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after search/filter.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  // Toggle mutation with optimistic update + rollback across every cached probe/page
  // query under the frameworks key (the adaptive grid shards the cache by page/search).
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string | number; enabled: boolean }) =>
      complianceApi.setFrameworkEnabled(id, enabled),
    onMutate: async ({ id, enabled }) => {
      await queryClient.cancelQueries({ queryKey: FRAMEWORKS_QUERY_KEY });
      const previous = queryClient.getQueriesData<PagedResult<ComplianceFramework>>({
        queryKey: FRAMEWORKS_QUERY_KEY,
      });
      queryClient.setQueriesData<PagedResult<ComplianceFramework>>(
        { queryKey: FRAMEWORKS_QUERY_KEY },
        (old) =>
          old
            ? { ...old, items: old.items.map((f) => (f.id === id ? { ...f, isEnabled: enabled } : f)) }
            : old
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FRAMEWORKS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['compliance-frameworks-full'] });
    },
  });

  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as PageTab);
  };

  // Unauthenticated is a whole-page state (mirrors the pre-migration behaviour) — distinct
  // from the four-state list discipline below, which governs only the list body.
  if (isError && isUnauthorizedError(error)) {
    return (
      <div className={styles.page}>
        <PageHeader title="Compliance Frameworks" subtitle="Compliance framework mappings and coverage analysis" />
        <UnauthenticatedBanner feature="compliance frameworks" />
      </div>
    );
  }

  const columnCount = showEnabledColumn ? 8 : 7;

  let listBody: React.ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load compliance frameworks." onRetry={refetch} />;
  } else if (frameworks.length === 0) {
    listBody = (
      <EmptyState
        icon={<DocumentRegular />}
        title="No Frameworks"
        description="Frameworks ready for activation. Enable a framework's controls below, or configure enforcement policies to start evaluating them."
        actionLabel="Go to Policies"
        onAction={() => navigate('/guardrails')}
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Compliance frameworks" style={{ minWidth: '900px' }}>
          <TableHeader>
            <TableRow>
              {showEnabledColumn && <TableHeaderCell>Enabled</TableHeaderCell>}
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'code' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('code')}
                onClick={() => toggleSort('code')}
              >
                Code
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('name')}
                onClick={() => toggleSort('name')}
                style={{ width: '100%' }}
              >
                Name
              </TableHeaderCell>
              <TableHeaderCell>Version</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'controlCount' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('controlCount')}
                onClick={() => toggleSort('controlCount')}
              >
                Controls
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'complianceScore' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('complianceScore')}
                onClick={() => toggleSort('complianceScore')}
              >
                Coverage
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'complianceScore' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('complianceScore')}
                onClick={() => toggleSort('complianceScore')}
              >
                Compliance Score
              </TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(1) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {frameworks.map((item) => {
              const score = item.complianceScore ?? 0;
              const satisfied = Math.round(item.controlCount * (score / 100));
              return (
                <TableRow key={item.id}>
                  {showEnabledColumn && (
                    <TableCell>
                      <Switch
                        checked={!!item.isEnabled}
                        disabled={toggleMutation.isPending}
                        onChange={(_, data) => toggleMutation.mutate({ id: item.id, enabled: data.checked })}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge appearance="outline">{item.code}</Badge>
                  </TableCell>
                  <TableCell>
                    <Body1 style={{ fontWeight: 500 }}>{item.name}</Body1>
                  </TableCell>
                  <TableCell>
                    <Body2>{item.version || '-'}</Body2>
                  </TableCell>
                  <TableCell>
                    <Body2>{item.controlCount}</Body2>
                  </TableCell>
                  <TableCell>
                    <Body2>
                      {satisfied}/{item.controlCount} controls
                    </Body2>
                  </TableCell>
                  <TableCell>
                    <div className={styles.scoreCell}>
                      <ProgressBar value={score / 100} thickness="large" style={{ width: '100px' }} />
                      <Body2 style={{ color: getComplianceColor(score) }}>
                        {item.complianceScore != null && !isNaN(item.complianceScore)
                          ? `${item.complianceScore}%`
                          : 'Not yet calculated'}
                      </Body2>
                      <ConfidenceBadge score={Math.min(100, score + 10)} size="small" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      appearance="subtle"
                      icon={<ChevronRightRegular />}
                      onClick={() => navigate(`/frameworks/${item.referenceId}`)}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.nonListSection}>
        <PageHeader title="Compliance Frameworks" subtitle="Compliance framework mappings and coverage analysis" />
      </div>

      {/* Coverage Radar — shows enabled frameworks. Non-list content; always sourced from the
          full framework set, never the migrated list's current page/search/sort. */}
      {frameworkList.filter((f) => f.isEnabled).length >= 2 && (
        <div className={styles.nonListSection}>
          <ComplianceCoverageRadar
            frameworks={frameworkList
              .filter((f) => f.isEnabled)
              .map((f) => ({
                name: f.code,
                coverage: (f.complianceScore ?? 0) / 100,
                target: 0.9,
                controlsTotal: f.controlCount,
                controlsSatisfied: Math.round(f.controlCount * ((f.complianceScore ?? 0) / 100)),
              }))}
            onFrameworkClick={(name) => {
              const fw = frameworkList.find((f) => f.code === name);
              if (fw) navigate(`/frameworks/${fw.referenceId}`);
            }}
          />
        </div>
      )}

      <Card className={styles.tabsCard}>
        <div className={styles.tabHeader}>
          <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
            <Tab value="frameworks">Frameworks</Tab>
            <Tab value="mappings" icon={<LinkRegular />}>Control Mappings</Tab>
          </TabList>
        </div>

        {selectedTab === 'frameworks' && (
          <div className={styles.listSection}>
            <ListPageLayout
              toolbar={
                <ListToolbar
                  search={state.search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search frameworks…"
                  onRefresh={refetch}
                  isRefreshing={isFetching}
                  testIdPrefix="frameworks"
                />
              }
              footer={
                <Pager
                  page={state.page}
                  totalPages={totalPages}
                  hasPreviousPage={hasPreviousPage}
                  hasNextPage={hasNextPage}
                  totalCount={totalCount}
                  onPageChange={setPage}
                  testId="frameworks-pager"
                />
              }
            >
              {listBody}
            </ListPageLayout>
          </div>
        )}

        {selectedTab === 'mappings' && (
          <div className={styles.mappingsSection}>
            <ControlMappingsTab frameworkCodes={frameworkCodes} />
          </div>
        )}
      </Card>
    </div>
  );
};
